import { and, condition, type FilterNode, or } from './filter-ast';
import type { OrderBy } from './record-query';

/**
 * THE DASHBOARD'S PANEL PREDICATES (`docs/decisions/dashboard.md` D4).
 *
 * ONE IMPLEMENTATION, TWO CALLERS, AND THAT IS THE WHOLE POINT (INV-11). `shared` ships
 * source with no build step, so the SEED (server, writing the "See all" views) and the
 * PANEL (browser, drawing the rows) import the same function and cannot disagree at
 * birth. A tile is its panel's query with a count instead of a page, and that rule is
 * only real if there is one place the query is written.
 *
 * THESE ARE NOT SAVED VIEWS, deliberately. A saved view is per-object and the attention
 * panel reads two objects; a saved view cannot express the day rail at all, because
 * relative date operands are refused on DATETIME fields; and views are one shared set any
 * member may edit, so a panel titled "Needs attention" whose filter a colleague changed
 * on Tuesday is a screen nobody can trust.
 *
 * EVERY BUILDER TAKES RESOLVED IDS AND NOTHING ELSE. How they are resolved differs by
 * caller, and the asymmetry is deliberate rather than sloppy:
 *
 *  - the SEED resolves by universal id, because it owns the model it is writing;
 *  - the BROWSER resolves the OBJECT by api name and then its fields by name within
 *    that object, because a caller cannot set a universal id on a created object at all
 *    and a universal-id-keyed dashboard would therefore be untestable (D16).
 *
 * SELECT OPTIONS ARE THE ONE PLACE THE TWO LOOKUPS CANNOT SHARE A KEY, and it is a
 * deviation worth naming. An option id is `opt_<sha256(fieldUniversalId|key)>`, minted
 * by the seed with node's crypto - a browser cannot reproduce it, and the option KEY is
 * not on the wire at all (`SelectOptionDef` carries `id`, `label`, `color`, `position`).
 * So the seed resolves options by KEY and the browser by LABEL, and both lists live
 * below so the two cannot drift. A renamed option label drops its condition, which lands
 * the panel in the ABSENT state D19 already defines - the same blast radius as D16's
 * api-name resolution and for the same reason.
 */

// --- the panel set (D3, D5) --------------------------------------------------

export const DASHBOARD_PANELS = ['attention', 'today', 'openItems', 'money'] as const;
export type PanelId = (typeof DASHBOARD_PANELS)[number];

export interface PanelDefinition {
  readonly id: PanelId;
  /** The Customize list's row, and the card's heading stem. */
  readonly label: string;
  /**
   * The api name this panel's rows come from, or `null` for a panel over a PLATFORM
   * table, which exists on every installation and therefore always resolves.
   *
   * That word "record-backed" is doing work in D16: without it a bare platform install
   * would render a dashboard consisting of one task panel saying "nothing due today" -
   * technically true, and a worse front door than the redirect it replaced.
   */
  readonly objectName: string | null;
}

/**
 * THE ORDER IS THE PRODUCT'S (D1). A user says WHICH of these they see; nobody says
 * where one goes, how big it is, or what is in it. That is a checkbox list, not a
 * layout editor, and reordering is the seam's next step rather than this one.
 *
 * The array order is also D5's CUT ORDER read backwards, which makes it the order a
 * person is most likely to untick.
 */
export const DASHBOARD_PANEL_DEFINITIONS: readonly PanelDefinition[] = [
  { id: 'attention', label: 'Needs Attention', objectName: 'cohort_enrollment' },
  { id: 'today', label: 'Today', objectName: 'call' },
  { id: 'openItems', label: 'Open Items', objectName: null },
  { id: 'money', label: 'Money', objectName: 'payment' },
];

/**
 * LANE B's object, which is NOT the attention panel's object (D6).
 *
 * The panel is two stacked lanes over two objects, never a union: they have two shapes
 * and two id spaces, a merged list could not say what a row IS, and a merged count would
 * double-count every overlap. Lane A decides whether the panel resolves at all; lane B
 * is a rendering condition on top of it, and an empty lane renders no heading.
 */
export const ATTENTION_LANE_B_OBJECT_NAME = 'agency';

/** D33's one stored key, and the shape stored under it. HIDDEN, never visible. */
export const DASHBOARD_PANELS_PREFERENCE_KEY = 'dashboard.panels';

export interface DashboardPanelsPreference {
  readonly hidden: readonly PanelId[];
}

/**
 * Reads the stored value back, defensively, because it is untyped jsonb somebody's
 * browser wrote months ago.
 *
 * AN UNKNOWN ID IS IGNORED RATHER THAN AN ERROR. Storing the SUBTRACTION is what makes a
 * panel added later visible to everybody by default; ignoring ids nobody recognises is
 * the other half of that promise, so retiring a panel cannot break a stored row.
 */
export function readHiddenPanels(value: unknown): readonly PanelId[] {
  if (typeof value !== 'object' || value === null) return [];
  const hidden = (value as { hidden?: unknown }).hidden;
  if (!Array.isArray(hidden)) return [];
  return DASHBOARD_PANELS.filter((panel) => hidden.includes(panel));
}

// --- option vocabularies, shared so the two resolvers cannot drift ------------

/**
 * `cohort_enrollment.health`, WHICH IS NOW THE SYSTEM'S VERDICT (`phase-b-coach.md` P1).
 *
 * It used to be the colour a coach typed. It is computed as of Phase B, from rules that are
 * configuration, and the coach's own judgement lives beside it in `coach_assessment` with a
 * date and a validity. Nothing about these option lists changes: the field is the same
 * SELECT with the same three options, and only the hand that writes it moved.
 */
export const HEALTH_RED_OPTION = { key: 'red', label: 'Red' } as const;
export const HEALTH_YELLOW_OPTION = { key: 'yellow', label: 'Yellow' } as const;
/** D15's lane A: both colours that mean "look at this", worst first. */
export const HEALTH_ATTENTION_OPTIONS = [HEALTH_RED_OPTION, HEALTH_YELLOW_OPTION] as const;
/** `cohort_enrollment.enrollment_status`: a finished run is history, not attention. */
export const ENROLLMENT_ACTIVE_OPTION = { key: 'active', label: 'Active' } as const;
/**
 * `agency.lifecycle_status`: are they a customer. An alumni agency flagged six months
 * ago is history too, and this field is the single owner of that question. The status
 * "Paused" is gone (redesign register A14): pausing is `agency.paused_until`.
 */
export const AGENCY_CURRENT_OPTIONS = [
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'active', label: 'Active' },
] as const;
/**
 * `payment.payment_status` values that are SETTLED. Refunded and written off never set
 * `paid_at`, so without this the Money panel would tell the owner he is owed money nobody
 * intends to pay. Paid is here too (redesign register D8, owner decision of 2026-10-03): a
 * payment somebody marked Paid without typing the date is paid, and listing it as owed
 * money would be the same false alarm.
 */
export const PAYMENT_SETTLED_OPTIONS = [
  { key: 'paid', label: 'Paid' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'written_off', label: 'Written off' },
] as const;

// --- lane A: health red (D6, D7) ---------------------------------------------

export interface HealthRedIds {
  readonly health: string;
  readonly healthRedOptionId: string;
  readonly enrollmentStatus: string;
  readonly enrollmentActiveOptionId: string;
}

export function healthRedFilter(ids: HealthRedIds): FilterNode {
  return and(
    condition(ids.health, 'eq', ids.healthRedOptionId),
    condition(ids.enrollmentStatus, 'eq', ids.enrollmentActiveOptionId),
  );
}

/** The STALEST score first: a red set twelve days ago is the one nobody looked at. */
export function healthRedOrderBy(ids: { readonly healthSetAt: string }): readonly OrderBy[] {
  return [{ fieldId: ids.healthSetAt, direction: 'asc', nulls: 'first' }];
}

/**
 * WHY LANE A'S EMPTY STATE COSTS TWO MORE COUNTS (D22).
 *
 * If nobody is setting `health`, lane A is empty and the dashboard looks broken - when
 * the truth is that the work has not been done. "No enrollment is red. 4 of 12 active
 * enrollments have no health score" is the single most useful sentence on the screen,
 * and it is two `withTotalCount` queries that fire only when lane A comes back empty.
 */
export function activeEnrollmentsFilter(ids: {
  readonly enrollmentStatus: string;
  readonly enrollmentActiveOptionId: string;
}): FilterNode {
  return condition(ids.enrollmentStatus, 'eq', ids.enrollmentActiveOptionId);
}

export function unscoredEnrollmentsFilter(ids: {
  readonly enrollmentStatus: string;
  readonly enrollmentActiveOptionId: string;
  readonly health: string;
}): FilterNode {
  return and(activeEnrollmentsFilter(ids), condition(ids.health, 'isEmpty'));
}

// --- the Phase B lanes (phase-b-coach.md D15) --------------------------------

/**
 * THE TWO NEW LANE BUILDERS, BESIDE THE OLD ONES RATHER THAN INSTEAD OF THEM.
 *
 * D15 replaces what the attention panel reads: lane A becomes red AND yellow ordered by the
 * day the colour started, and lane B stops being the AI flag - that becomes a health rule
 * with a reason (`flag_with_recency`) and reappears inside lane A - and becomes the
 * enrollments whose computed colour is worse than green with no assessment behind it.
 *
 * They ship here and are wired up in the slice that also ships the engine behind
 * `needs_assessment`. A panel rewired before the field is computed would read an empty
 * column and render an empty lane, which is D19's ABSENT/EMPTY collapse: "nothing waits"
 * told where the truth is "nothing computes this yet".
 *
 * BOTH CARRY THE ACTIVE-ENROLLMENT CONDITION the builders above carry, and D15 does not
 * mention it. It is kept deliberately: a churned customer who was red in March is history,
 * not attention, and dropping the condition would fill the coach's morning with people who
 * left. `enrollment_status` is the single owner of that question in this model.
 */
export interface AttentionLaneAIds {
  readonly health: string;
  readonly healthAttentionOptionIds: readonly string[];
  readonly enrollmentStatus: string;
  readonly enrollmentActiveOptionId: string;
}

export function attentionLaneAFilter(ids: AttentionLaneAIds): FilterNode {
  return and(
    condition(ids.health, 'in', [...ids.healthAttentionOptionIds]),
    condition(ids.enrollmentStatus, 'eq', ids.enrollmentActiveOptionId),
  );
}

/**
 * THE OLDEST TROUBLE FIRST, and `health_since` is what makes that honest: it is the day the
 * colour STARTED, not the day the engine last ran. `health_computed_at` moves every night on
 * every row, so ordering by it would shuffle the lane daily and say nothing.
 */
export function attentionLaneAOrderBy(ids: { readonly healthSince: string }): readonly OrderBy[] {
  return [{ fieldId: ids.healthSince, direction: 'asc', nulls: 'first' }];
}

export interface AttentionLaneBIds {
  readonly needsAssessment: string;
  readonly enrollmentStatus: string;
  readonly enrollmentActiveOptionId: string;
}

/**
 * "The system says this is not green and no human has looked at it since." `needs_assessment`
 * is computed (D13), so this is a plain equality on a BOOLEAN rather than the three-way
 * comparison between findings, an assessment and its validity that produced it.
 */
export function attentionLaneBFilter(ids: AttentionLaneBIds): FilterNode {
  return and(
    condition(ids.needsAssessment, 'eq', true),
    condition(ids.enrollmentStatus, 'eq', ids.enrollmentActiveOptionId),
  );
}

// --- lane B: flagged by AI (D6, D8) ------------------------------------------

export interface AiFlaggedIds {
  readonly aiRiskFlag: string;
  readonly lifecycleStatus: string;
  readonly currentLifecycleOptionIds: readonly string[];
}

export function aiFlaggedFilter(ids: AiFlaggedIds): FilterNode {
  return and(
    condition(ids.aiRiskFlag, 'eq', true),
    condition(ids.lifecycleStatus, 'in', [...ids.currentLifecycleOptionIds]),
  );
}

export function aiFlaggedOrderBy(ids: { readonly aiRiskFlaggedAt: string }): readonly OrderBy[] {
  return [{ fieldId: ids.aiRiskFlaggedAt, direction: 'asc', nulls: 'first' }];
}

// --- the day rail (D9, D10) --------------------------------------------------

export interface DayBounds {
  /** ISO-8601 WITH an offset, which is what a DATETIME operand demands. */
  readonly from: string;
  readonly to: string;
}

/**
 * A calendar day as a pair of instants IN THE READER'S OWN TIMEZONE.
 *
 * THIS IS THE MODULE THAT HAD TO CLOSE THE TIMEZONE QUESTION. `record-page-and-views.md`
 * recorded it as "a decision nobody has taken" and recommended the browser for v1,
 * "because the person reading the view is the person whose week it is"; `dueDate.ts`
 * already took that position for tasks. The day rail is the first surface that cannot
 * avoid it, so it settles it the same way, in the same direction, in one place.
 *
 * IT DOES NOT EXTEND RELATIVE DATE TOKENS TO DATETIME FIELDS, and the view validator
 * stays exactly as it is. Its refusal is about a STORED view, read by many people in
 * unknown places; this is computed at render time in one browser. Different problem.
 *
 * The `Date(y, m, d)` constructor rather than millisecond arithmetic: it re-resolves the
 * UTC offset for that day, so the bounds of a DST-transition day carry the offsets that
 * day actually had.
 */
export function dayBounds(dayKey: string): DayBounds {
  const [year, month, day] = dayKey.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`not a YYYY-MM-DD day: ${dayKey}`);
  }
  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day, 23, 59, 59, 999);
  return { from: withOffset(start), to: withOffset(end) };
}

/**
 * `2026-08-03T00:00:00.000+02:00`. Never `toISOString()`, which would render the instant
 * in UTC and hand a filter a day boundary an hour or two off in most of the world.
 */
function withOffset(date: Date): string {
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absolute = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
  return (
    `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `.${pad(date.getMilliseconds(), 3)}${offset}`
  );
}

export interface CallDayIds {
  readonly scheduledAt: string;
  readonly occurredAt: string;
}

/**
 * Calls whose `scheduled_at` OR `occurred_at` falls in the selected day (D9).
 *
 * BOTH ENDS, and the panel would be half a panel with one: a call somebody logged after
 * the fact has an `occurred_at` and no `scheduled_at`, and it is exactly what the
 * Happened block is for. D10 restates this filter as `scheduled_at between [lo, hi]`
 * while discussing the BOUNDS; D9 defines the panel, and the panel needs the pair.
 */
export function callsOnDayFilter(ids: CallDayIds, bounds: DayBounds): FilterNode {
  const window = [bounds.from, bounds.to];
  return or(
    condition(ids.scheduledAt, 'between', window),
    condition(ids.occurredAt, 'between', window),
  );
}

export function callsOnDayOrderBy(ids: { readonly scheduledAt: string }): readonly OrderBy[] {
  return [{ fieldId: ids.scheduledAt, direction: 'asc', nulls: 'first' }];
}

// --- money (D14) -------------------------------------------------------------

/**
 * The window, as constants rather than settings.
 *
 * The lower bound stops a 2019 payment somebody forgot to tick from appearing forever;
 * the upper bound is the week ahead. Only the upper one appears in the copy: the floor
 * changes nothing a person can act on, and "last 12 months" in a tile qualifier invites
 * the question "what about the older ones" that the panel does not want to have.
 */
export const MONEY_WINDOW_BACK_DAYS = 365;
export const MONEY_WINDOW_FORWARD_DAYS = 7;

export interface DateWindow {
  readonly from: string;
  readonly to: string;
}

/** Calendar arithmetic on a `YYYY-MM-DD`, for the reason `resolveRelativeDate` gives. */
export function moneyWindow(todayKey: string): DateWindow {
  const [year, month, day] = todayKey.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`not a YYYY-MM-DD day: ${todayKey}`);
  }
  const shift = (days: number): string => {
    const moved = new Date(year, month - 1, day + days);
    const pad = (value: number, width = 2) => String(value).padStart(width, '0');
    return `${pad(moved.getFullYear(), 4)}-${pad(moved.getMonth() + 1)}-${pad(moved.getDate())}`;
  };
  return { from: shift(-MONEY_WINDOW_BACK_DAYS), to: shift(MONEY_WINDOW_FORWARD_DAYS) };
}

export interface MoneyIds {
  readonly paidAt: string;
  readonly dueDate: string;
  readonly paymentStatus: string;
  readonly settledOptionIds: readonly string[];
}

/**
 * Unpaid, in the window, and not settled by some other route.
 *
 * THE STATUS CLAUSE IS AN EXPLICIT `or` WITH `isEmpty`, AND THAT IS NOT DEFENSIVE STYLE.
 * Both filter backends drop nulls on `notIn` - the SQL compiler ANDs a not-null presence
 * check into it and the JS evaluator agrees, which the conformance corpus states outright
 * - so a bare `notIn` would hide every payment whose status is null. Nothing in this
 * engine is required, so an unstatused payment is the NORMAL case during data entry, and
 * the naive filter would have hidden exactly the rows somebody had just typed and not
 * finished. Test obligation 4 pins it.
 */
export function outstandingPaymentsFilter(ids: MoneyIds, window: DateWindow): FilterNode {
  return and(
    condition(ids.paidAt, 'isEmpty'),
    condition(ids.dueDate, 'between', [window.from, window.to]),
    unsettledStatus(ids),
  );
}

/**
 * The same predicate MINUS the date window, for a STORED view (section 8's third seeded
 * view) - and it is the module's one deviation from the document, named here rather than
 * buried.
 *
 * D14 asks the seeded view to carry "D14's filter verbatim". The platform cannot store
 * it. A stored view expresses a live date range only through a relative operand, whose
 * token set is CLOSED - `today`, `this_week`, `next_7_days`, `this_month`,
 * `last_30_days` - and none of them is `[today - 365d, today + 7d]`; `between` is the
 * only operator a token may carry, so the two bounds cannot be written separately
 * either. The remaining option is literal dates computed at seed time, which is exactly
 * the "wrong at every midnight and nothing updates it" failure the data model refused
 * when it deleted the `overdue` status.
 *
 * So the view is a strict SUPERSET of the panel: every outstanding payment, unwindowed.
 * The consequence is carried in the panel's footer, which says "See All"
 * with NO number - a footer link opens the set its number counted, or it says something
 * else. The tile keeps counting the panel's own query, so D15's rule is untouched.
 */
export function outstandingPaymentsStoredFilter(ids: MoneyIds): FilterNode {
  return and(condition(ids.paidAt, 'isEmpty'), unsettledStatus(ids));
}

function unsettledStatus(ids: MoneyIds): FilterNode {
  return or(
    condition(ids.paymentStatus, 'isEmpty'),
    condition(ids.paymentStatus, 'notIn', [...ids.settledOptionIds]),
  );
}

export function outstandingPaymentsOrderBy(ids: { readonly dueDate: string }): readonly OrderBy[] {
  return [{ fieldId: ids.dueDate, direction: 'asc', nulls: 'last' }];
}
